// Shared MapLibre layer-filter builders for the time-scrubber'd
// hazard layers. Extracted from MapView.tsx as part of the refactor
// pass (Step 5 of 5) so MapView only owns the React/component shell.
//
// Three filter families:
//
//   buildGfaTimeFilter(hazard, offsetH)
//     Per-hazard match AND a validMs-in-[center +/- 1.5h] check.
//     The GFA proxy returns four frames at -3/0/+3/+6h; this filter
//     picks exactly the frame whose validMs is closest to the slider.
//
//   buildValidityWindowFilter(offsetH)
//     For SIGMET / CWA features that carry a [validFromMs, validToMs]
//     range. The slider's sample point must lie inside that range.
//     SIGMETs and CWAs are nowcasts \u2014 they don't evolve hourly.
//
//   buildFlightCatFilter(offsetH)
//     For wxstations features (METAR + TAF frames). Same +/-1.5h
//     validMs window as buildGfaTimeFilter, minus the hazard match.
//
// Plus a tile-URL builder for NEXRAD (Iowa State Mesonet) since the
// scrubber there is a separate concept (5-min frames over the past
// hour).

import type { Map as MlMap } from 'maplibre-gl';

/** Half the G-AIRMET frame spacing. AWC publishes frames at 3h
 *  intervals; making the per-frame visibility window 1.5h on either
 *  side of `validMs` means each slider position shows EXACTLY ONE
 *  frame (no overlap, no gap). */
export const FRAME_HALF_MS = 1.5 * 3600 * 1000;

/** Build a MapLibre layer filter that combines the per-hazard match
 *  AND a window check: the slider's wall-clock sample point must lie
 *  inside [validMs - 1.5h, validMs + 1.5h]. Features missing
 *  timestamps match unconditionally so a defensive cache hit never
 *  blank-screens the map. */
export function buildGfaTimeFilter(hazard: string, offsetH: number): unknown {
  const center = Date.now() + offsetH * 3_600_000;
  return [
    'all',
    ['==', ['get', 'hazard'], hazard],
    [
      'any',
      // Legacy / missing timestamps -> always show.
      ['!', ['has', 'validMs']],
      [
        'all',
        ['>=', ['to-number', ['get', 'validMs']], center - FRAME_HALF_MS],
        ['<=', ['to-number', ['get', 'validMs']], center + FRAME_HALF_MS],
      ],
    ],
  ];
}

/** Validity-window filter for SIGMET / CWA layers. */
export function buildValidityWindowFilter(offsetH: number): unknown {
  const center = Date.now() + offsetH * 3_600_000;
  return [
    'any',
    // Defensive fallback when the upstream didn't enrich timestamps.
    ['!', ['has', 'validFromMs']],
    [
      'all',
      ['<=', ['to-number', ['coalesce', ['get', 'validFromMs'], center]], center],
      ['>=', ['to-number', ['coalesce', ['get', 'validToMs'], center]], center],
    ],
  ];
}

/** Per-station-frame filter for wxstations-derived layers. */
export function buildFlightCatFilter(offsetH: number): unknown {
  const center = Date.now() + offsetH * 3_600_000;
  return [
    'any',
    ['!', ['has', 'validMs']],
    [
      'all',
      ['>=', ['to-number', ['get', 'validMs']], center - FRAME_HALF_MS],
      ['<=', ['to-number', ['get', 'validMs']], center + FRAME_HALF_MS],
    ],
  ];
}

/** Apply buildValidityWindowFilter to one or more layers in lockstep.
 *  Skips layers that don't exist yet (sources are added lazily). */
export function applyValidityWindowFilter(
  map: MlMap,
  layerIds: ReadonlyArray<string>,
  offsetH: number,
): void {
  const filter = buildValidityWindowFilter(offsetH);
  for (const id of layerIds) {
    if (!map.getLayer(id)) continue;
    map.setFilter(id, filter as any);
  }
}

/** Apply buildFlightCatFilter to one or more wxstations-derived
 *  layers (Surface Winds, Visibility, Ceiling, etc.). */
export function applyStationLayerFilter(
  map: MlMap,
  layerIds: ReadonlyArray<string>,
  offsetH: number,
): void {
  const filter = buildFlightCatFilter(offsetH);
  for (const id of layerIds) {
    if (!map.getLayer(id)) continue;
    map.setFilter(id, filter as any);
  }
}

/** Build the Iowa State Mesonet NEXRAD tile URL for a given minute
 *  offset from now. offsetMin must be non-positive; 0 returns the
 *  live frame (which Mesonet auto-refreshes server-side every ~5
 *  min), and negative values use the archived per-timestamp template.
 *  Mesonet rounds timestamps down to the nearest 5-minute slot, so
 *  we do the same here to avoid a wasted miss on misaligned slots.
 *
 *  `variant` picks which radar product to fetch (ForeFlight-style trio,
 *  added 2026-05-21):
 *    'classic'   = NEXRAD Level III base reflectivity composite (n0q).
 *                  This is what we've shipped historically; the time
 *                  scrubber works only for this variant.
 *    'composite' = MRMS Composite Reflectivity QC'd via our /api/tile
 *                  proxy → NCEP geoserver WMS. Current-time only.
 *    'lowtilt'   = MRMS Hybrid-Scan Reflectivity (closest-to-ground beam),
 *                  Iowa Mesonet q2-hsr. Current-time only. */
export type NexradVariant = 'classic' | 'composite' | 'lowtilt';

export function nexradTileUrl(offsetMin: number, variant: NexradVariant = 'classic'): string {
  // HI-7 audit 2026-05-22: all radar variants now go through our edge
  // tile proxy (/api/tile) so they pick up the same SW cache,
  // wall-clock upstream timeout, and CORS handling as the FAA charts.
  // Previously only 'composite' was proxied; 'classic' and 'lowtilt'
  // were direct cross-origin fetches to iastate.edu and would freeze
  // MapLibre when that host went slow on a flaky LTE link.
  if (variant === 'composite') {
    return '/api/tile?layer=radar-composite&z={z}&x={x}&y={y}';
  }
  if (variant === 'lowtilt') {
    return '/api/tile?layer=radar-lowtilt&z={z}&x={x}&y={y}';
  }
  // 'classic' — NEXRAD Level III base reflectivity.
  if (!Number.isFinite(offsetMin) || offsetMin >= 0) {
    return '/api/tile?layer=radar-classic&z={z}&x={x}&y={y}';
  }
  // Historical 5-minute slot. The Iowa Mesonet ridge product is
  // timestamped; we keep the timestamp param but route via the proxy
  // so we still get our SW cache + bounded upstream. Use a query
  // parameter so the allowlist + URL builder don't need a special
  // case for every 5-minute slot.
  const slot = Math.floor((Date.now() + offsetMin * 60_000) / (5 * 60_000)) * (5 * 60_000);
  const d = new Date(slot);
  const ts =
    String(d.getUTCFullYear())
    + String(d.getUTCMonth() + 1).padStart(2, '0')
    + String(d.getUTCDate()).padStart(2, '0')
    + String(d.getUTCHours()).padStart(2, '0')
    + String(d.getUTCMinutes()).padStart(2, '0');
  return `/api/tile?layer=radar-classic&ts=${ts}&z={z}&x={x}&y={y}`;
}
